"use client";

import { useEffect, useRef } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  LayoutGrid,
  MousePointer2,
  Wrench,
} from "lucide-react";
import { BrandGlobe } from "@/components/landing/BrandGlobe";
import { PeakChart } from "@/components/landing/PeakChart";
import { Timeline } from "@/components/landing/Timeline";
import { WelcomeDialog } from "@/components/landing/WelcomeDialog";
import {
  CookieSettingsLink,
  LanguageSwitcher,
  MockupShowcase,
  renderAccented,
  ScrollRevealText,
  type MockupPanel,
} from "@/components/landing/sections";
import { APP_LOGIN_URL, BOOK_DEMO_URL } from "@/lib/links";
import { localePath, type Locale } from "@/lib/i18n";

// All page copy, keyed by locale. Mockup headings use *asterisks* for the
// shared showcase's serif accent.
const COPY = {
  sv: {
    private: "Privatperson",
    business: "Företag",
    help: "Hjälp",
    partnerPortal: "Partnerportal",
    nav: { platform: "Plattform", forInstallers: "För installatörer", pricing: "Priser" },
    h1: "Energiplattformen för installatörer och energibolag",
    becomePartner: "Bli partner",
    trustedBy: "Betrodd av installatörer och partners:",
    scrollReveal:
      "Installatörer och energibolag bygger sin affär på förtroende. Samvolt Intelligence ger er verktygen att leverera mer – fjärrstyrning, övervakning och AI-optimering för varje anläggning ni sätter upp, samlat i en plattform ni kan lita på.",
    bookADemo: "Boka en demo",
    bookDemo: "Boka demo",
    mockupPanels: [
      {
        heading: "Hela kundportföljen i en *vy*",
        body: "Övervaka alla dina anläggningar samlat – sol, batteri, förbrukning och drift i realtid. Upptäck avvikelser innan kunden hör av sig.",
      },
      {
        heading: "Driftsätt på *minuter*",
        body: "En guidad process kopplar växelriktare och batteri, sätter tariffer och lägger till kunden. För Solinteg och Sunwoda – utan krångel.",
      },
      {
        heading: "Färre serviceärenden, *nöjdare* kunder",
        body: "Automatisk AI-styrning och proaktiv övervakning håller anläggningarna i toppform. Mindre support för er, mer värde för kunden.",
      },
      {
        heading: "Er profil, *ert* varumärke",
        body: "Bygg upplevelsen kring er egen identitet med anpassningsbara vyer och widgets – så att appen känns som en del av ert erbjudande.",
      },
    ],
    cards: {
      install: { title: "Snabb installation", subtitle: "Driftsatt på minuter" },
      monitoring: { title: "Fjärrövervakning", subtitle: "Hela portföljen i realtid" },
      revenue: { title: "Återkommande intäkt", subtitle: "Abonnemang per anläggning" },
      peaks: {
        title: "Effekttoppar",
        body: "Erbjud effekttoppskapning som tjänst – sänk kundens nätavgift automatiskt och stärk ert erbjudande.",
      },
      sweden: {
        title: "Utvecklad i Sverige",
        body: "Stöd för Solinteg och Sunwoda – byggt för nordiska installationer, med data inom EU",
      },
    },
    cta: {
      label: "Bli partner",
      heading: "Väx din affär med smartare energi",
      body: "Boka en demo och se hur Samvolt Intelligence hjälper er driftsätta snabbare, supporta mindre och skapa återkommande intäkter – på varje anläggning.",
    },
    footer: {
      blurb: "Intelligent energistyrning för sol, batteri och elförbrukning.",
      platform: "Plattform",
      forInstallers: "För installatörer",
      monitoring: "Fjärrövervakning",
      pricing: "Priser",
      company: "Företag",
      about: "Om oss",
      careers: "Karriär",
      resources: "Resurser",
      documentation: "Dokumentation",
      support: "Support",
      status: "Status",
      rights: "© 2026 Samvolt Intelligence. Alla rättigheter förbehållna.",
      privacy: "Integritetspolicy",
      terms: "Användarvillkor",
    },
  },
  en: {
    private: "Homeowner",
    business: "Business",
    help: "Help",
    partnerPortal: "Partner portal",
    nav: { platform: "Platform", forInstallers: "For installers", pricing: "Pricing" },
    h1: "The energy platform for installers and energy companies",
    becomePartner: "Become a partner",
    trustedBy: "Trusted by installers and partners:",
    scrollReveal:
      "Installers and energy companies build their business on trust. Samvolt Intelligence gives you the tools to deliver more – remote control, monitoring and AI optimization for every installation you set up, gathered in a platform you can rely on.",
    bookADemo: "Book a demo",
    bookDemo: "Book a demo",
    mockupPanels: [
      {
        heading: "Your whole customer portfolio in one *view*",
        body: "Monitor all your installations in one place – solar, battery, consumption and operations in real time. Spot deviations before the customer calls.",
      },
      {
        heading: "Commission in *minutes*",
        body: "A guided flow connects inverter and battery, sets tariffs and adds the customer. For Solinteg and Sunwoda – without hassle.",
      },
      {
        heading: "Fewer service tickets, *happier* customers",
        body: "Automatic AI control and proactive monitoring keep installations in top shape. Less support for you, more value for the customer.",
      },
      {
        heading: "Your profile, *your* brand",
        body: "Build the experience around your own identity with customizable views and widgets – so the app feels like part of your offering.",
      },
    ],
    cards: {
      install: { title: "Fast installation", subtitle: "Commissioned in minutes" },
      monitoring: { title: "Remote monitoring", subtitle: "The whole portfolio in real time" },
      revenue: { title: "Recurring revenue", subtitle: "Subscription per installation" },
      peaks: {
        title: "Power peaks",
        body: "Offer peak shaving as a service – automatically lower your customers' grid fees and strengthen your offering.",
      },
      sweden: {
        title: "Developed in Sweden",
        body: "Supports Solinteg and Sunwoda – built for Nordic installations, with data kept within the EU",
      },
    },
    cta: {
      label: "Become a partner",
      heading: "Grow your business with smarter energy",
      body: "Book a demo and see how Samvolt Intelligence helps you commission faster, support less and create recurring revenue – on every installation.",
    },
    footer: {
      blurb: "Intelligent energy management for solar, battery and electricity consumption.",
      platform: "Platform",
      forInstallers: "For installers",
      monitoring: "Remote monitoring",
      pricing: "Pricing",
      company: "Company",
      about: "About us",
      careers: "Careers",
      resources: "Resources",
      documentation: "Documentation",
      support: "Support",
      status: "Status",
      rights: "© 2026 Samvolt Intelligence. All rights reserved.",
      privacy: "Privacy policy",
      terms: "Terms of use",
    },
  },
  no: {
    private: "Privatperson",
    business: "Bedrift",
    help: "Hjelp",
    partnerPortal: "Partnerportal",
    nav: { platform: "Plattform", forInstallers: "For installatører", pricing: "Priser" },
    h1: "Energiplattformen for installatører og energiselskaper",
    becomePartner: "Bli partner",
    trustedBy: "Betrodd av installatører og partnere:",
    scrollReveal:
      "Installatører og energiselskaper bygger virksomheten sin på tillit. Samvolt Intelligence gir dere verktøyene til å levere mer – fjernstyring, overvåking og AI-optimalisering for hvert anlegg dere setter opp, samlet i én plattform dere kan stole på.",
    bookADemo: "Bestill en demo",
    bookDemo: "Bestill demo",
    mockupPanels: [
      {
        heading: "Hele kundeporteføljen i én *oversikt*",
        body: "Overvåk alle anleggene dine på ett sted – sol, batteri, forbruk og drift i sanntid. Oppdag avvik før kunden tar kontakt.",
      },
      {
        heading: "Sett i drift på *minutter*",
        body: "En veiledet prosess kobler til vekselretter og batteri, setter tariffer og legger til kunden. For Solinteg og Sunwoda – uten styr.",
      },
      {
        heading: "Færre servicesaker, *fornøyde* kunder",
        body: "Automatisk AI-styring og proaktiv overvåking holder anleggene i toppform. Mindre support for dere, mer verdi for kunden.",
      },
      {
        heading: "Deres profil, *deres* merkevare",
        body: "Bygg opplevelsen rundt deres egen identitet med tilpassbare visninger og widgets – slik at appen føles som en del av tilbudet deres.",
      },
    ],
    cards: {
      install: { title: "Rask installasjon", subtitle: "I drift på minutter" },
      monitoring: { title: "Fjernovervåking", subtitle: "Hele porteføljen i sanntid" },
      revenue: { title: "Gjentakende inntekt", subtitle: "Abonnement per anlegg" },
      peaks: {
        title: "Effekttopper",
        body: "Tilby effekttoppkapping som tjeneste – senk kundens nettleie automatisk og styrk tilbudet deres.",
      },
      sweden: {
        title: "Utviklet i Sverige",
        body: "Støtter Solinteg og Sunwoda – bygget for nordiske installasjoner, med data lagret innenfor EU",
      },
    },
    cta: {
      label: "Bli partner",
      heading: "Voks virksomheten med smartere energi",
      body: "Bestill en demo og se hvordan Samvolt Intelligence hjelper dere med å sette i drift raskere, supportere mindre og skape gjentakende inntekter – på hvert anlegg.",
    },
    footer: {
      blurb: "Intelligent energistyring for sol, batteri og strømforbruk.",
      platform: "Plattform",
      forInstallers: "For installatører",
      monitoring: "Fjernovervåking",
      pricing: "Priser",
      company: "Selskap",
      about: "Om oss",
      careers: "Karriere",
      resources: "Ressurser",
      documentation: "Dokumentasjon",
      support: "Support",
      status: "Status",
      rights: "© 2026 Samvolt Intelligence. Med enerett.",
      privacy: "Personvernerklæring",
      terms: "Vilkår for bruk",
    },
  },
  da: {
    private: "Privatperson",
    business: "Erhverv",
    help: "Hjælp",
    partnerPortal: "Partnerportal",
    nav: { platform: "Platform", forInstallers: "For installatører", pricing: "Priser" },
    h1: "Energiplatformen for installatører og energiselskaber",
    becomePartner: "Bliv partner",
    trustedBy: "Betroet af installatører og partnere:",
    scrollReveal:
      "Installatører og energiselskaber bygger deres forretning på tillid. Samvolt Intelligence giver jer værktøjerne til at levere mere – fjernstyring, overvågning og AI-optimering for hvert anlæg, I opsætter, samlet i én platform, I kan stole på.",
    bookADemo: "Book en demo",
    bookDemo: "Book demo",
    mockupPanels: [
      {
        heading: "Hele kundeporteføljen i ét *overblik*",
        body: "Overvåg alle jeres anlæg ét sted – sol, batteri, forbrug og drift i realtid. Opdag afvigelser, før kunden henvender sig.",
      },
      {
        heading: "Idriftsæt på *minutter*",
        body: "Et guidet forløb tilslutter inverter og batteri, indstiller tariffer og tilføjer kunden. Til Solinteg og Sunwoda – uden besvær.",
      },
      {
        heading: "Færre servicesager, *gladere* kunder",
        body: "Automatisk AI-styring og proaktiv overvågning holder anlæggene i topform. Mindre support for jer, mere værdi for kunden.",
      },
      {
        heading: "Jeres profil, *jeres* brand",
        body: "Byg oplevelsen op omkring jeres egen identitet med tilpasselige visninger og widgets – så appen føles som en del af jeres tilbud.",
      },
    ],
    cards: {
      install: { title: "Hurtig installation", subtitle: "Idriftsat på minutter" },
      monitoring: { title: "Fjernovervågning", subtitle: "Hele porteføljen i realtid" },
      revenue: { title: "Tilbagevendende indtægt", subtitle: "Abonnement pr. anlæg" },
      peaks: {
        title: "Effekttoppe",
        body: "Tilbyd effekttopskapning som en tjeneste – sænk kundens netafgift automatisk og styrk jeres tilbud.",
      },
      sweden: {
        title: "Udviklet i Sverige",
        body: "Understøtter Solinteg og Sunwoda – bygget til nordiske installationer, med data holdt inden for EU",
      },
    },
    cta: {
      label: "Bliv partner",
      heading: "Væks jeres forretning med smartere energi",
      body: "Book en demo og se, hvordan Samvolt Intelligence hjælper jer med at idriftsætte hurtigere, supportere mindre og skabe tilbagevendende indtægter – på hvert anlæg.",
    },
    footer: {
      blurb: "Intelligent energistyring til sol, batteri og elforbrug.",
      platform: "Platform",
      forInstallers: "For installatører",
      monitoring: "Fjernovervågning",
      pricing: "Priser",
      company: "Virksomhed",
      about: "Om os",
      careers: "Karriere",
      resources: "Ressourcer",
      documentation: "Dokumentation",
      support: "Support",
      status: "Status",
      rights: "© 2026 Samvolt Intelligence. Alle rettigheder forbeholdes.",
      privacy: "Privatlivspolitik",
      terms: "Brugsvilkår",
    },
  },
  fi: {
    private: "Yksityishenkilö",
    business: "Yritys",
    help: "Ohje",
    partnerPortal: "Kumppaniportaali",
    nav: { platform: "Alusta", forInstallers: "Asentajille", pricing: "Hinnat" },
    h1: "Energia-alusta asentajille ja energiayhtiöille",
    becomePartner: "Ryhdy kumppaniksi",
    trustedBy: "Asentajien ja kumppaneiden luottama:",
    scrollReveal:
      "Asentajat ja energiayhtiöt rakentavat liiketoimintansa luottamukselle. Samvolt Intelligence antaa teille työkalut toimittaa enemmän – etäohjaus, valvonta ja tekoälyoptimointi jokaiselle pystyttämällenne kohteelle, koottuna yhdelle alustalle, johon voitte luottaa.",
    bookADemo: "Varaa demo",
    bookDemo: "Varaa demo",
    mockupPanels: [
      {
        heading: "Koko asiakaskanta yhdessä *näkymässä*",
        body: "Valvo kaikkia kohteitasi yhdessä paikassa – aurinko, akku, kulutus ja käyttö reaaliajassa. Havaitse poikkeamat ennen kuin asiakas ottaa yhteyttä.",
      },
      {
        heading: "Käyttöönotto *minuuteissa*",
        body: "Opastettu kulku yhdistää invertterin ja akun, asettaa tariffit ja lisää asiakkaan. Solintegille ja Sunwodalle – ilman vaivaa.",
      },
      {
        heading: "Vähemmän huoltopyyntöjä, *tyytyväisemmät* asiakkaat",
        body: "Automaattinen tekoälyohjaus ja ennakoiva valvonta pitävät kohteet huippukunnossa. Vähemmän tukea teille, enemmän arvoa asiakkaalle.",
      },
      {
        heading: "Teidän profiilinne, *teidän* brändinne",
        body: "Rakenna kokemus oman identiteettinne ympärille mukautettavilla näkymillä ja widgeteillä – niin että sovellus tuntuu osalta tarjontaanne.",
      },
    ],
    cards: {
      install: { title: "Nopea asennus", subtitle: "Käyttöönotto minuuteissa" },
      monitoring: { title: "Etävalvonta", subtitle: "Koko kanta reaaliajassa" },
      revenue: { title: "Toistuva tuotto", subtitle: "Tilaus per kohde" },
      peaks: {
        title: "Tehohuiput",
        body: "Tarjoa tehohuippujen leikkaus palveluna – laske asiakkaan verkkomaksuja automaattisesti ja vahvista tarjontaanne.",
      },
      sweden: {
        title: "Kehitetty Ruotsissa",
        body: "Tukee Solintegia ja Sunwodaa – rakennettu pohjoismaisiin asennuksiin, tiedot säilytetään EU:n sisällä",
      },
    },
    cta: {
      label: "Ryhdy kumppaniksi",
      heading: "Kasvata liiketoimintaasi älykkäämmällä energialla",
      body: "Varaa demo ja katso, miten Samvolt Intelligence auttaa teitä ottamaan käyttöön nopeammin, tukemaan vähemmän ja luomaan toistuvaa tuottoa – jokaisessa kohteessa.",
    },
    footer: {
      blurb: "Älykäs energianhallinta aurinkoenergialle, akulle ja sähkönkulutukselle.",
      platform: "Alusta",
      forInstallers: "Asentajille",
      monitoring: "Etävalvonta",
      pricing: "Hinnat",
      company: "Yritys",
      about: "Tietoa meistä",
      careers: "Ura",
      resources: "Resurssit",
      documentation: "Dokumentaatio",
      support: "Tuki",
      status: "Tila",
      rights: "© 2026 Samvolt Intelligence. Kaikki oikeudet pidätetään.",
      privacy: "Tietosuojakäytäntö",
      terms: "Käyttöehdot",
    },
  },
  de: {
    private: "Privatperson",
    business: "Unternehmen",
    help: "Hilfe",
    partnerPortal: "Partnerportal",
    nav: { platform: "Plattform", forInstallers: "Für Installateure", pricing: "Preise" },
    h1: "Die Energieplattform für Installateure und Energieunternehmen",
    becomePartner: "Partner werden",
    trustedBy: "Vertraut von Installateuren und Partnern:",
    scrollReveal:
      "Installateure und Energieunternehmen bauen ihr Geschäft auf Vertrauen auf. Samvolt Intelligence gibt Ihnen die Werkzeuge, um mehr zu liefern – Fernsteuerung, Überwachung und KI-Optimierung für jede Anlage, die Sie aufbauen, gebündelt in einer Plattform, auf die Sie sich verlassen können.",
    bookADemo: "Demo buchen",
    bookDemo: "Demo buchen",
    mockupPanels: [
      {
        heading: "Ihr gesamtes Kundenportfolio in einer *Ansicht*",
        body: "Überwachen Sie alle Ihre Anlagen an einem Ort – Solar, Batterie, Verbrauch und Betrieb in Echtzeit. Erkennen Sie Abweichungen, bevor der Kunde sich meldet.",
      },
      {
        heading: "Inbetriebnahme in *Minuten*",
        body: "Ein geführter Ablauf verbindet Wechselrichter und Batterie, legt Tarife fest und fügt den Kunden hinzu. Für Solinteg und Sunwoda – ohne Aufwand.",
      },
      {
        heading: "Weniger Servicefälle, *zufriedenere* Kunden",
        body: "Automatische KI-Steuerung und proaktive Überwachung halten die Anlagen in Bestform. Weniger Support für Sie, mehr Wert für den Kunden.",
      },
      {
        heading: "Ihr Profil, *Ihre* Marke",
        body: "Gestalten Sie das Erlebnis rund um Ihre eigene Identität mit anpassbaren Ansichten und Widgets – damit sich die App wie ein Teil Ihres Angebots anfühlt.",
      },
    ],
    cards: {
      install: { title: "Schnelle Installation", subtitle: "In Minuten in Betrieb" },
      monitoring: { title: "Fernüberwachung", subtitle: "Das gesamte Portfolio in Echtzeit" },
      revenue: { title: "Wiederkehrende Einnahmen", subtitle: "Abonnement pro Anlage" },
      peaks: {
        title: "Lastspitzen",
        body: "Bieten Sie Lastspitzenkappung als Service an – senken Sie die Netzentgelte Ihrer Kunden automatisch und stärken Sie Ihr Angebot.",
      },
      sweden: {
        title: "Entwickelt in Schweden",
        body: "Unterstützt Solinteg und Sunwoda – gebaut für nordische Installationen, mit Daten innerhalb der EU",
      },
    },
    cta: {
      label: "Partner werden",
      heading: "Wachsen Sie mit smarterer Energie",
      body: "Buchen Sie eine Demo und sehen Sie, wie Samvolt Intelligence Ihnen hilft, schneller in Betrieb zu nehmen, weniger zu supporten und wiederkehrende Einnahmen zu schaffen – bei jeder Anlage.",
    },
    footer: {
      blurb: "Intelligentes Energiemanagement für Solar, Batterie und Stromverbrauch.",
      platform: "Plattform",
      forInstallers: "Für Installateure",
      monitoring: "Fernüberwachung",
      pricing: "Preise",
      company: "Unternehmen",
      about: "Über uns",
      careers: "Karriere",
      resources: "Ressourcen",
      documentation: "Dokumentation",
      support: "Support",
      status: "Status",
      rights: "© 2026 Samvolt Intelligence. Alle Rechte vorbehalten.",
      privacy: "Datenschutzrichtlinie",
      terms: "Nutzungsbedingungen",
    },
  },
  cs: {
    private: "Soukromá osoba",
    business: "Firma",
    help: "Nápověda",
    partnerPortal: "Partnerský portál",
    nav: { platform: "Platforma", forInstallers: "Pro instalatéry", pricing: "Ceny" },
    h1: "Energetická platforma pro instalatéry a energetické společnosti",
    becomePartner: "Staňte se partnerem",
    trustedBy: "Důvěřují nám instalatéři a partneři:",
    scrollReveal:
      "Instalatéři a energetické společnosti staví své podnikání na důvěře. Samvolt Intelligence vám dává nástroje, abyste dodávali více – dálkové ovládání, monitoring a AI optimalizaci pro každou instalaci, kterou nastavíte, shromážděné v jedné platformě, na kterou se můžete spolehnout.",
    bookADemo: "Objednat demo",
    bookDemo: "Objednat demo",
    mockupPanels: [
      {
        heading: "Celé portfolio zákazníků v jednom *pohledu*",
        body: "Sledujte všechny své instalace na jednom místě – solár, baterii, spotřebu a provoz v reálném čase. Odhalte odchylky dříve, než zavolá zákazník.",
      },
      {
        heading: "Uvedení do provozu za *minuty*",
        body: "Průvodce připojí střídač a baterii, nastaví tarify a přidá zákazníka. Pro Solinteg a Sunwoda – bez potíží.",
      },
      {
        heading: "Méně servisních požadavků, *spokojenější* zákazníci",
        body: "Automatické AI řízení a proaktivní monitoring udržují instalace ve špičkové kondici. Méně podpory pro vás, více hodnoty pro zákazníka.",
      },
      {
        heading: "Váš profil, *vaše* značka",
        body: "Vytvořte zážitek kolem vlastní identity s přizpůsobitelnými pohledy a widgety – aby aplikace působila jako součást vaší nabídky.",
      },
    ],
    cards: {
      install: { title: "Rychlá instalace", subtitle: "V provozu za minuty" },
      monitoring: { title: "Vzdálený monitoring", subtitle: "Celé portfolio v reálném čase" },
      revenue: { title: "Opakující se příjem", subtitle: "Předplatné za instalaci" },
      peaks: {
        title: "Špičky výkonu",
        body: "Nabídněte ořezávání špiček jako službu – automaticky snižte síťové poplatky zákazníků a posilte svou nabídku.",
      },
      sweden: {
        title: "Vyvinuto ve Švédsku",
        body: "Podporuje Solinteg a Sunwoda – vytvořeno pro severské instalace, s daty uchovávanými v rámci EU",
      },
    },
    cta: {
      label: "Staňte se partnerem",
      heading: "Rozšiřte své podnikání s chytřejší energií",
      body: "Objednejte si demo a uvidíte, jak vám Samvolt Intelligence pomáhá rychleji uvádět do provozu, méně podporovat a vytvářet opakující se příjmy – u každé instalace.",
    },
    footer: {
      blurb: "Inteligentní správa energie pro solár, baterii a spotřebu elektřiny.",
      platform: "Platforma",
      forInstallers: "Pro instalatéry",
      monitoring: "Vzdálený monitoring",
      pricing: "Ceny",
      company: "Společnost",
      about: "O nás",
      careers: "Kariéra",
      resources: "Zdroje",
      documentation: "Dokumentace",
      support: "Podpora",
      status: "Stav",
      rights: "© 2026 Samvolt Intelligence. Všechna práva vyhrazena.",
      privacy: "Zásady ochrany osobních údajů",
      terms: "Podmínky použití",
    },
  },
  zh: {
    private: "个人用户",
    business: "企业",
    help: "帮助",
    partnerPortal: "合作伙伴门户",
    nav: { platform: "平台", forInstallers: "面向安装商", pricing: "价格" },
    h1: "面向安装商和能源公司的能源平台",
    becomePartner: "成为合作伙伴",
    trustedBy: "受安装商和合作伙伴信赖：",
    scrollReveal:
      "安装商和能源公司以信任为基础开展业务。Samvolt Intelligence 为您提供交付更多价值的工具——为您安装的每一处系统提供远程控制、监控和 AI 优化，全部汇聚于一个值得信赖的平台。",
    bookADemo: "预约演示",
    bookDemo: "预约演示",
    mockupPanels: [
      {
        heading: "在一个*视图*中掌握全部客户组合",
        body: "在一个地方监控您的所有系统——实时查看光伏、电池、用电和运行状态。在客户来电之前发现异常。",
      },
      {
        heading: "*几分钟*即可完成调试",
        body: "引导式流程连接逆变器和电池、设置电价并添加客户。适用于 Solinteg 和 Sunwoda——省心无忧。",
      },
      {
        heading: "更少的服务工单，*更满意*的客户",
        body: "自动 AI 控制和主动监控让系统保持最佳状态。您需要更少的支持，客户获得更多价值。",
      },
      {
        heading: "您的形象，*您的*品牌",
        body: "通过可定制的视图和小组件，围绕您自己的品牌打造体验——让应用成为您产品的一部分。",
      },
    ],
    cards: {
      install: { title: "快速安装", subtitle: "几分钟内完成调试" },
      monitoring: { title: "远程监控", subtitle: "实时掌握整个组合" },
      revenue: { title: "经常性收入", subtitle: "按系统订阅" },
      peaks: {
        title: "功率峰值",
        body: "以服务形式提供削峰功能——自动降低客户的电网费用，增强您的产品竞争力。",
      },
      sweden: {
        title: "瑞典研发",
        body: "支持 Solinteg 和 Sunwoda——为北欧安装打造，数据保存在欧盟境内",
      },
    },
    cta: {
      label: "成为合作伙伴",
      heading: "用更智能的能源发展您的业务",
      body: "预约演示，了解 Samvolt Intelligence 如何帮助您更快调试、减少支持工作，并在每一处系统上创造经常性收入。",
    },
    footer: {
      blurb: "面向光伏、电池和用电的智能能源管理。",
      platform: "平台",
      forInstallers: "面向安装商",
      monitoring: "远程监控",
      pricing: "价格",
      company: "公司",
      about: "关于我们",
      careers: "招贤纳士",
      resources: "资源",
      documentation: "文档",
      support: "支持",
      status: "状态",
      rights: "© 2026 Samvolt Intelligence. 保留所有权利。",
      privacy: "隐私政策",
      terms: "使用条款",
    },
  },
} satisfies Record<Locale, unknown>;

// Background video that plays only while its containing card is hovered.
// Drop it as the first child of a card; it auto-wires to that card's hover.
function HoverVideo({ src, className }: { src: string; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    const card = video?.parentElement;
    if (!video || !card) return;
    const showFrame = () => {
      try {
        video.currentTime = 0.1;
      } catch {
        /* not seekable yet */
      }
    };
    video.addEventListener("loadedmetadata", showFrame);
    const play = () => {
      video.currentTime = 0;
      void video.play();
    };
    const pause = () => {
      video.pause();
      showFrame();
    };
    card.addEventListener("mouseenter", play);
    card.addEventListener("mouseleave", pause);
    return () => {
      video.removeEventListener("loadedmetadata", showFrame);
      card.removeEventListener("mouseenter", play);
      card.removeEventListener("mouseleave", pause);
    };
  }, []);
  return (
    <>
      <video
        ref={ref}
        className={`${className ?? ""} brightness-[0.6] transition-[filter] duration-500 ease-out group-hover:brightness-100`}
        src={src}
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden
      />
      <span className="pointer-events-none absolute left-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-0">
        <MousePointer2 size={15} strokeWidth={2} />
      </span>
    </>
  );
}

const mockupVideos: { video: string; textSide: "left" | "right" }[] = [
  { video: "/home.webm", textSide: "left" },
  { video: "/scheme.webm", textSide: "right" },
  { video: "/savings.webm", textSide: "left" },
  { video: "/widget.webm", textSide: "right" },
];

export default function CompanyPage({ locale = "sv" }: { locale?: Locale }) {
  const t = COPY[locale];

  const mockupPanels: MockupPanel[] = t.mockupPanels.map((panel, i) => ({
    ...panel,
    ...mockupVideos[i],
  }));

  const partners = [
    { name: "Ahlsell", src: "/partners/ahlsell.svg" },
    { name: "Rexel", src: "/partners/rexel.svg" },
    { name: "KP", src: "/partners/kp.webp" },
    { name: "Horizon", src: "/partners/horizon-logo.png" },
  ];

  return (
    <main className="relative w-full bg-black font-sans text-white">
      {/* Consent has to be asked wherever a visitor can land, not just on / */}
      <WelcomeDialog locale={locale} />

      {/* First viewport: header + hero fill the screen exactly */}
      <div className="flex h-svh flex-col">
        {/* ============================================================
          TOP UTILITY BAR
          ============================================================ */}
        <div className="relative z-50 flex h-8 items-center justify-between px-5 md:px-7">
          <div className="flex items-center gap-4">
            <a
              href={localePath(locale, "/")}
              className="text-[11px] tracking-tight text-white/50 transition-colors hover:text-white"
            >
              {t.private}
            </a>
            <span className="h-3 w-px bg-white/20" aria-hidden="true" />
            <a
              href={localePath(locale, "/company")}
              className="text-[11px] tracking-tight text-white transition-colors hover:text-white"
            >
              {t.business}
            </a>
          </div>
          <div className="flex items-center gap-6">
            <a
              href={localePath(locale, "/support")}
              className="text-[11px] tracking-tight text-white/60 transition-colors hover:text-white"
            >
              {t.help}
            </a>
            <a
              href={APP_LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] tracking-tight text-white/60 transition-colors hover:text-white"
            >
              {t.partnerPortal}
              <ArrowUpRight size={10} strokeWidth={2} />
            </a>
            <LanguageSwitcher locale={locale} path="/company" />
          </div>
        </div>

        {/* ============================================================
          HERO – inset, rounded card with white padding around it
          ============================================================ */}
        <div className="min-h-0 flex-1 p-1.5 pt-0 md:p-2 md:pt-0">
          <section className="relative h-full min-h-[28rem] w-full overflow-hidden rounded-[16px] md:rounded-[22px]">
            {/* Background video */}
            <video
              className="absolute inset-0 h-full w-full object-cover brightness-120"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            >
              <source
                src="/hero-videos/hero_video_av1.webm"
                type='video/webm; codecs="av01.0.05M.08"'
              />
              <source src="/hero-videos/hero_video_mp4.mp4" type="video/mp4" />
            </video>

            {/* Content */}
            <div className="relative z-10 flex h-full flex-col">
              {/* Main nav – inside the hero card, over the video */}
              <header className="relative flex items-center justify-between px-5 pt-5 md:px-7 md:pt-6">
                <a href={localePath(locale, "/")} className="inline-flex items-center">
                  <img
                    src="/intelligence-logo.svg"
                    alt="Samvolt Intelligence"
                    className="h-5 w-auto md:h-6"
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                </a>

                <nav className="hidden items-center gap-8 text-sm md:flex">
                  <a href="#plattform" className="text-white/70 transition-colors hover:text-white">
                    {t.nav.platform}
                  </a>
                  <a href="#plattform" className="text-white/70 transition-colors hover:text-white">
                    {t.nav.forInstallers}
                  </a>
                  <a
                    href={BOOK_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/70 transition-colors hover:text-white"
                  >
                    {t.nav.pricing}
                  </a>
                </nav>
              </header>

              <div className="flex-1" />

              {/* Hero bottom row */}
              <div className="flex items-end justify-between gap-6 px-5 pb-7 md:px-7 md:pb-8">
                <div>
                  <h1 className="max-w-3xl text-balance font-display text-5xl font-normal leading-[1.02] tracking-[-0.035em] text-white md:text-6xl lg:max-w-4xl lg:text-7xl 2xl:max-w-6xl 2xl:text-8xl">
                    {t.h1}
                  </h1>
                  <a
                    href={BOOK_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-8 inline-flex items-center rounded-[4px] border border-white/15 bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur-md transition-colors hover:bg-white/20"
                  >
                    {t.becomePartner}
                    <span className="grid grid-cols-[0fr] opacity-0 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:ml-2.5 group-hover:grid-cols-[1fr] group-hover:opacity-100">
                      <span className="overflow-hidden">
                        <span className="flex h-6 w-6 items-center justify-center rounded-[3px] border border-white/25 text-white">
                          <ArrowRight size={13} strokeWidth={2} />
                        </span>
                      </span>
                    </span>
                  </a>
                </div>
              </div>

              {/* Logo carousel strip */}
              <section className="px-5 pb-6 md:px-7 md:pb-7">
                <p className="text-xs text-white/45">{t.trustedBy}</p>
                <div
                  className="relative mt-3 flex items-center overflow-hidden border-t border-white/10 pt-5"
                  style={{
                    WebkitMaskImage:
                      "linear-gradient(to right, transparent 0, black 72px, black calc(100% - 72px), transparent 100%)",
                    maskImage:
                      "linear-gradient(to right, transparent 0, black 72px, black calc(100% - 72px), transparent 100%)",
                  }}
                >
                  <div className="flex w-max animate-logo-scroll items-center">
                    {[...Array(6)]
                      .flatMap(() => partners)
                      .map((logo, index) => (
                        <div
                          key={index}
                          className="flex shrink-0 items-center justify-center px-7 sm:px-10"
                        >
                          <img
                            src={logo.src}
                            alt={logo.name}
                            className="h-4 w-auto max-w-none object-contain opacity-50 transition-opacity duration-300 hover:opacity-90 sm:h-5"
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </section>
            </div>
          </section>
        </div>
      </div>

      {/* ============================================================
          SCROLL-REVEAL INTRO + APP MOCKUP
          ============================================================ */}
      <div className="bg-white">
        <section className="pb-24">
          <div className="rounded-b-[28px] bg-black px-5 py-32 shadow-[0_60px_120px_-40px_rgba(0,0,0,0.55)] md:px-7 md:py-48">
            <div className="mx-auto max-w-4xl">
              <ScrollRevealText
                className="pb-8 font-sans text-[1.5rem] font-normal leading-[1.35] tracking-[-0.02em] sm:text-[1.875rem] md:text-[2.75rem] md:leading-[1.3]"
                text={t.scrollReveal}
              />
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-1.5 text-[1.25rem] font-medium text-white/55 transition-colors duration-300 ease-out hover:text-white md:text-[1.4375rem]"
              >
                <span className="pb-1 decoration-current underline-offset-4">{t.bookADemo}</span>
                <ArrowRight
                  size={18}
                  strokeWidth={2}
                  className="transition-transform duration-300 ease-out group-hover:translate-x-1"
                />
              </a>
            </div>
          </div>
        </section>

        <MockupShowcase panels={mockupPanels} />
      </div>

      {/* ============================================================
          BENTO WORK / FEATURE GRID
          ============================================================ */}
      <section id="plattform" className="scroll-mt-16 bg-white px-3 py-3 pb-16 md:px-4 md:py-4 md:pb-16">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {/* --- Card: Snabb installation (light) --- */}
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex h-[60svh] flex-col items-center justify-end overflow-hidden rounded-[8px] bg-gradient-to-br from-[#f5efe6] to-[#e7d8c2] p-5 transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:h-[84svh] sm:max-h-[820px]"
          >
            <HoverVideo
              className="absolute inset-0 h-full w-full object-cover"
              src="/531_1080x60_shots_so.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
            <span className="absolute right-4 top-4 z-10 flex h-8 w-8 translate-y-1 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-sm transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0 group-hover:opacity-100">
              <ArrowUpRight size={16} strokeWidth={2} />
            </span>
            <div className="relative z-10 flex w-80 max-w-full items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-7 py-3 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] backdrop-blur-md transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-[115%]">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[5px] bg-black text-white">
                <Wrench size={18} strokeWidth={1.75} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-sm font-medium text-white">{t.cards.install.title}</span>
                <span className="text-sm text-white/70">{t.cards.install.subtitle}</span>
              </span>
            </div>
          </a>

          {/* --- Card: Fjärrövervakning (dark) --- */}
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex h-[60svh] flex-col items-center justify-end overflow-hidden rounded-[8px] bg-gradient-to-br from-[#0f1f1a] to-[#13352b] p-5 transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:h-[84svh] sm:max-h-[820px]"
          >
            <HoverVideo
              className="absolute inset-0 h-full w-full object-cover"
              src="/864_1080x60_shots_so.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
            <span className="absolute right-4 top-4 z-10 flex h-8 w-8 translate-y-1 items-center justify-center rounded-full bg-white/15 text-white opacity-0 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0 group-hover:opacity-100">
              <ArrowUpRight size={16} strokeWidth={2} />
            </span>
            <div className="relative z-10 flex w-80 max-w-full items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-7 py-3 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] backdrop-blur-md transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-[115%]">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[5px] bg-black text-white">
                <LayoutGrid size={18} strokeWidth={1.75} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-sm font-medium text-white">{t.cards.monitoring.title}</span>
                <span className="text-sm text-white/60">{t.cards.monitoring.subtitle}</span>
              </span>
            </div>
          </a>

          {/* --- Card: Återkommande intäkt (image) --- */}
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex h-[60svh] flex-col items-center justify-end overflow-hidden rounded-[8px] bg-[#13352b] p-5 transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:h-[84svh] sm:max-h-[820px]"
          >
            <HoverVideo
              className="absolute inset-0 h-full w-full object-cover"
              src="/266_1080x60_shots_so.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
            <span className="absolute right-4 top-4 z-10 flex h-8 w-8 translate-y-1 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-sm transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0 group-hover:opacity-100">
              <ArrowUpRight size={16} strokeWidth={2} />
            </span>
            <div className="relative z-10 flex w-80 max-w-full items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-7 py-3 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] backdrop-blur-md transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-[115%]">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[5px] bg-black text-white">
                <Building2 size={18} strokeWidth={1.75} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-sm font-medium text-white">{t.cards.revenue.title}</span>
                <span className="text-sm text-white/70">{t.cards.revenue.subtitle}</span>
              </span>
            </div>
          </a>

          {/* --- Card: Effekttoppar (dark) --- */}
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex h-[48svh] flex-col overflow-hidden rounded-[8px] bg-gradient-to-br from-[#262626] to-[#161616] p-5 transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:h-[62svh] sm:max-h-[610px]"
          >
            <div className="relative z-10 mx-auto max-w-sm pt-2 text-center">
              <h3 className="text-balance font-display text-2xl font-normal leading-[1.1] tracking-[-0.02em] text-white md:text-3xl">
                {t.cards.peaks.title}
              </h3>
              <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-white/55">
                {t.cards.peaks.body}
              </p>
            </div>

            <PeakChart className="mt-5 min-h-0 flex-1" />
          </a>

          {/* --- Card: Utvecklad i Sverige (globe, wide) --- */}
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex h-[48svh] flex-col justify-start overflow-hidden rounded-[8px] bg-gradient-to-br from-[#262626] to-[#161616] p-5 transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:col-span-2 sm:h-[62svh] sm:max-h-[610px]"
          >
            <BrandGlobe sizeFactor={1.6} offsetY="58%" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

            <div className="relative z-10 mx-auto max-w-md pt-4 text-center">
              <h3 className="text-balance font-display text-3xl font-normal leading-[1.05] tracking-[-0.02em] text-white md:text-4xl">
                {t.cards.sweden.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60 md:text-base">
                {t.cards.sweden.body}
              </p>
            </div>
          </a>
        </div>
      </section>

      {/* ============================================================
          TIMELINE
          ============================================================ */}
      <div className="bg-gradient-to-b from-white via-[#f6f8f8] to-frost text-pine">
        <Timeline locale={locale} />
      </div>

      {/* ============================================================
          CTA + FOOTER
          ============================================================ */}
      <footer className="bg-black px-5 pb-8 pt-28 md:px-7 md:pt-36">
        <div className="mx-auto max-w-5xl text-center">
          <p className="text-sm tracking-tight text-white/40">{t.cta.label}</p>
          <h2 className="mx-auto mt-4 max-w-3xl text-balance font-display text-4xl font-medium leading-[1.05] tracking-[-0.03em] text-white md:text-6xl">
            {t.cta.heading}
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-white/55">
            {t.cta.body}
          </p>
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-9 inline-flex items-center rounded-[4px] bg-white px-6 py-3 text-sm font-medium text-black transition-colors hover:bg-white/90"
          >
            {t.bookDemo}
            <span className="grid grid-cols-[0fr] opacity-0 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:ml-2.5 group-hover:grid-cols-[1fr] group-hover:opacity-100">
              <span className="overflow-hidden">
                <span className="flex h-6 w-6 items-center justify-center rounded-[3px] border border-black/25 text-black">
                  <ArrowRight size={13} strokeWidth={2} />
                </span>
              </span>
            </span>
          </a>
        </div>

        {/* Footer links */}
        <div className="mx-auto mt-24 max-w-7xl border-t border-white/10 pt-10 md:mt-32">
          <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
            <div className="max-w-xs">
              <img src="/intelligence-logo.svg" alt="Samvolt Intelligence" className="h-5 w-auto" />
              <p className="mt-4 text-sm leading-relaxed text-white/40">
                {t.footer.blurb}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 sm:gap-16">
              <div className="flex flex-col gap-3">
                <span className="text-xs uppercase tracking-wider text-white/35">{t.footer.platform}</span>
                <a href="#plattform" className="text-sm text-white/65 transition-colors hover:text-white">
                  {t.footer.forInstallers}
                </a>
                <a href="#plattform" className="text-sm text-white/65 transition-colors hover:text-white">
                  {t.footer.monitoring}
                </a>
                <a
                  href={BOOK_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/65 transition-colors hover:text-white"
                >
                  {t.footer.pricing}
                </a>
              </div>
              <div className="flex flex-col gap-3">
                <span className="text-xs uppercase tracking-wider text-white/35">{t.footer.company}</span>
                <a href={localePath(locale, "/company")} className="text-sm text-white/65 transition-colors hover:text-white">
                  {t.footer.about}
                </a>
                <a href="mailto:support@samvolt.com" className="text-sm text-white/65 transition-colors hover:text-white">
                  {t.footer.careers}
                </a>
              </div>
              <div className="flex flex-col gap-3">
                <span className="text-xs uppercase tracking-wider text-white/35">{t.footer.resources}</span>
                <a href={localePath(locale, "/support")} className="text-sm text-white/65 transition-colors hover:text-white">
                  {t.footer.documentation}
                </a>
                <a href={localePath(locale, "/support")} className="text-sm text-white/65 transition-colors hover:text-white">
                  {t.footer.support}
                </a>
                <a href={localePath(locale, "/support")} className="text-sm text-white/65 transition-colors hover:text-white">
                  {t.footer.status}
                </a>
              </div>
            </div>
          </div>

          <div className="mt-14 flex flex-col gap-3 text-xs text-white/35 sm:flex-row sm:items-center sm:justify-between">
            <span>{t.footer.rights}</span>
            <div className="flex gap-6">
              {/* Legal documents exist in Swedish only */}
              <a href="/integritetspolicy" className="transition-colors hover:text-white/70">
                {t.footer.privacy}
              </a>
              <a href="/anvandarvillkor" className="transition-colors hover:text-white/70">
                {t.footer.terms}
              </a>
              <CookieSettingsLink locale={locale} />
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
